import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import AdminSponsorApplications from './AdminSponsorApplications';
import { CORE_SPORTS } from '@utils/sports';
import { SPONSORSHIP_TIERS, getTierLabel } from '@utils/sponsor';

interface SponsorRow {
  id: string;
  companyName: string;
  contactEmail?: string;
  country?: string;
  profileStatus?: string;
  preferredTier?: string;
  logoUrl?: string;
  isActive?: boolean;
}

interface OpportunityRow {
  id: string;
  title: string;
  opportunityType: string;
  sport?: string | null;
  sponsorshipTier?: string;
  status?: string;
  geography?: string;
  slotsAvailable?: number;
  description?: string;
}

interface ContestSponsorRow {
  id: string;
  sport: string;
  contestTaskId: string;
  sponsorId: string;
  sponsorName: string;
  sponsorshipTier?: string;
  displayLabel?: string;
}

interface ContestTaskOption {
  id: string;
  title: string;
  sport: string;
}

const authHeaders = () => {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AdminSponsorManagement: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [sponsors, setSponsors] = useState<SponsorRow[]>([]);
  const [opportunities, setOpportunities] = useState<OpportunityRow[]>([]);
  const [placements, setPlacements] = useState<ContestSponsorRow[]>([]);
  const [contestTasks, setContestTasks] = useState<ContestTaskOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [oppDialogOpen, setOppDialogOpen] = useState(false);
  const [oppForm, setOppForm] = useState({
    title: '',
    description: '',
    opportunityType: 'contest',
    sport: 'Badminton',
    sponsorshipTier: 'supporting',
    status: 'open',
    geography: 'global',
    slotsAvailable: 1,
  });

  const [placementDialogOpen, setPlacementDialogOpen] = useState(false);
  const [placementForm, setPlacementForm] = useState({
    sport: 'Badminton',
    contestTaskId: '',
    sponsorId: '',
    sponsorshipTier: 'supporting',
    displayLabel: 'Presented by',
    displayOrder: 0,
  });

  const loadSponsors = async () => {
    const res = await fetch('/api/admin/sponsors', { headers: authHeaders() });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    setSponsors(data.data || []);
  };

  const loadOpportunities = async () => {
    const res = await fetch('/api/admin/sponsorship-opportunities', { headers: authHeaders() });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    setOpportunities(data.data || []);
  };

  const loadPlacements = async () => {
    const res = await fetch('/api/admin/contest-sponsors', { headers: authHeaders() });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    setPlacements(data.data || []);
  };

  const loadContestTasks = async (sport: string) => {
    const res = await fetch(`/api/admin/contest-tasks?sport=${encodeURIComponent(sport)}`, { headers: authHeaders() });
    const data = await res.json();
    if (data.success && Array.isArray(data.data)) {
      setContestTasks(data.data.map((t: any) => ({ id: t.id, title: t.title, sport: t.sport || sport })));
    } else {
      setContestTasks([]);
    }
  };

  const refreshTab = async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 1) await loadSponsors();
      if (tab === 2) await loadOpportunities();
      if (tab === 3) {
        await Promise.all([loadPlacements(), loadSponsors()]);
        await loadContestTasks(placementForm.sport);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 0) return;
    void refreshTab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (tab === 3) void loadContestTasks(placementForm.sport);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placementForm.sport, tab]);

  const saveOpportunity = async () => {
    const res = await fetch('/api/admin/sponsorship-opportunities', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({
        title: oppForm.title,
        description: oppForm.description,
        opportunityType: oppForm.opportunityType,
        sport: oppForm.sport,
        sponsorshipTier: oppForm.sponsorshipTier,
        status: oppForm.status,
        geography: oppForm.geography,
        slotsAvailable: oppForm.slotsAvailable,
      }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    setOppDialogOpen(false);
    void loadOpportunities();
  };

  const toggleOpportunityStatus = async (id: string, status: string) => {
    await fetch(`/api/admin/sponsorship-opportunities/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ status }),
    });
    void loadOpportunities();
  };

  const updateSponsorStatus = async (id: string, profileStatus: string) => {
    await fetch(`/api/admin/sponsors/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ profileStatus }),
    });
    void loadSponsors();
  };

  const savePlacement = async () => {
    const res = await fetch('/api/admin/contest-sponsors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(placementForm),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    setPlacementDialogOpen(false);
    void loadPlacements();
  };

  const deletePlacement = async (id: string) => {
    await fetch(`/api/admin/contest-sponsors/${id}`, { method: 'DELETE', headers: authHeaders() });
    void loadPlacements();
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.5 }}>
        Sponsor management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Leads, partner profiles, published opportunities, and contest logo placements.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Leads & applications" sx={{ fontWeight: 700, textTransform: 'none' }} />
        <Tab label="Partner profiles" sx={{ fontWeight: 700, textTransform: 'none' }} />
        <Tab label="Opportunities" sx={{ fontWeight: 700, textTransform: 'none' }} />
        <Tab label="Contest placements" sx={{ fontWeight: 700, textTransform: 'none' }} />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {tab === 0 && <AdminSponsorApplications />}

      {tab === 1 && (
        <Box>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button startIcon={<RefreshIcon />} onClick={() => void refreshTab()}>Refresh</Button>
          </Stack>
          {loading ? (
            <CircularProgress />
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 800 }}>Company</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Contact</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Tier</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sponsors.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{s.companyName}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.country}</Typography>
                      </TableCell>
                      <TableCell>{s.contactEmail}</TableCell>
                      <TableCell>{getTierLabel(s.preferredTier)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={s.profileStatus || 'pending'} color={s.profileStatus === 'verified' ? 'success' : 'warning'} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Button size="small" onClick={() => void updateSponsorStatus(s.id, 'verified')}>Verify</Button>
                          <Button size="small" color="warning" onClick={() => void updateSponsorStatus(s.id, 'pending')}>Pending</Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {tab === 2 && (
        <Box>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
            <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOppDialogOpen(true)}>
              Publish opportunity
            </Button>
            <Button startIcon={<RefreshIcon />} onClick={() => void refreshTab()}>Refresh</Button>
          </Stack>
          {loading ? (
            <CircularProgress />
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 800 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Type / Sport</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Tier</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {opportunities.map((o) => (
                    <TableRow key={o.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{o.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{o.geography}</Typography>
                      </TableCell>
                      <TableCell>{o.opportunityType} · {o.sport || 'All'}</TableCell>
                      <TableCell>{getTierLabel(o.sponsorshipTier)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={o.status} color={o.status === 'open' ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {o.status !== 'open' && (
                            <Button size="small" onClick={() => void toggleOpportunityStatus(o.id, 'open')}>Open</Button>
                          )}
                          {o.status === 'open' && (
                            <Button size="small" onClick={() => void toggleOpportunityStatus(o.id, 'closed')}>Close</Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {tab === 3 && (
        <Box>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
            <Button startIcon={<AddIcon />} variant="contained" onClick={() => setPlacementDialogOpen(true)}>
              Link sponsor to contest
            </Button>
            <Button startIcon={<RefreshIcon />} onClick={() => void refreshTab()}>Refresh</Button>
          </Stack>
          {loading ? (
            <CircularProgress />
          ) : placements.length === 0 ? (
            <Alert severity="info">No contest sponsors linked yet. Assign a sponsor to show logos on contest cards.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 800 }}>Sport</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Sponsor</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Tier / Label</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Contest task</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {placements.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>{p.sport}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{p.sponsorName}</TableCell>
                      <TableCell>{getTierLabel(p.sponsorshipTier)} · {p.displayLabel}</TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{p.contestTaskId.slice(0, 8)}…</Typography>
                      </TableCell>
                      <TableCell>
                        <Button size="small" color="error" onClick={() => void deletePlacement(p.id)}>Remove</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      <Dialog open={oppDialogOpen} onClose={() => setOppDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Publish sponsorship opportunity</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" fullWidth value={oppForm.title} onChange={(e) => setOppForm({ ...oppForm, title: e.target.value })} />
            <TextField label="Description" fullWidth multiline rows={3} value={oppForm.description} onChange={(e) => setOppForm({ ...oppForm, description: e.target.value })} />
            <FormControl fullWidth>
              <InputLabel>Sport</InputLabel>
              <Select label="Sport" value={oppForm.sport} onChange={(e) => setOppForm({ ...oppForm, sport: String(e.target.value) })}>
                {CORE_SPORTS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Tier</InputLabel>
              <Select label="Tier" value={oppForm.sponsorshipTier} onChange={(e) => setOppForm({ ...oppForm, sponsorshipTier: String(e.target.value) })}>
                {SPONSORSHIP_TIERS.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOppDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void saveOpportunity().catch((e) => setError(e.message))}>Publish</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={placementDialogOpen} onClose={() => setPlacementDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Link sponsor to contest task</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Sport</InputLabel>
              <Select label="Sport" value={placementForm.sport} onChange={(e) => setPlacementForm({ ...placementForm, sport: String(e.target.value), contestTaskId: '' })}>
                {CORE_SPORTS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Contest task</InputLabel>
              <Select label="Contest task" value={placementForm.contestTaskId} onChange={(e) => setPlacementForm({ ...placementForm, contestTaskId: String(e.target.value) })}>
                {contestTasks.map((t) => <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Sponsor</InputLabel>
              <Select label="Sponsor" value={placementForm.sponsorId} onChange={(e) => setPlacementForm({ ...placementForm, sponsorId: String(e.target.value) })}>
                {sponsors.map((s) => <MenuItem key={s.id} value={s.id}>{s.companyName}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Tier</InputLabel>
              <Select label="Tier" value={placementForm.sponsorshipTier} onChange={(e) => setPlacementForm({ ...placementForm, sponsorshipTier: String(e.target.value) })}>
                {SPONSORSHIP_TIERS.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Display label" fullWidth value={placementForm.displayLabel} onChange={(e) => setPlacementForm({ ...placementForm, displayLabel: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlacementDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!placementForm.contestTaskId || !placementForm.sponsorId} onClick={() => void savePlacement().catch((e) => setError(e.message))}>
            Link sponsor
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSponsorManagement;
